import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { DataHubCredentials } from '../List/DataModels';
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { MultilingualTextField } from "@iconics/components";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextArea, TextField } from '@iconics/components';

class EditDataHubCredentials extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        const localStorageProvider = appContext().localStorageProvider;

        this.state =
        {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            connectionString: '',
            accessKey: '',
            concurrencyToken: '',
            canDelete: localStorageProvider.hasRight('DATAMODEL.Tenancy.Delete'), // displayName: TODO proper premission does not exist yet
            sections: [],
        };
    }

    private get id(): number
    {
        return Number.parseInt(this.props.params.id ?? '0');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_DataHubCredentials_PageTitle;
        this.backUrl = '/admin/data-hub-credentials';
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        await this.loadDataHubCredentials();
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>, _: Readonly<IState>): Promise<void>
    {
        if (!prevProps.params.id && this.props.params.id)
        {
            await this.loadDataHubCredentials();
        }
    }

    private async loadDataHubCredentials(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (this.props.params.id === undefined)
            {
                return;
            }
            const dataHubCredentialsData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.byId.get<DataHubCredentials>({
                id: this.props.params.id,
                tenancy: appContext().tenantId,
                select: DataHubCredentials,
            });
            await this.setStateAsync({
                name: dataHubCredentialsData.Name,
                displayName: this.languageValuesHelper.fromString(dataHubCredentialsData.DisplayName),
                connectionString: dataHubCredentialsData.ConnectionString,
                accessKey: dataHubCredentialsData.AccessKey,
                concurrencyToken: dataHubCredentialsData["@odata.etag"],
            });
        });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            if (!this.props.params.id)
            {
                return;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.byId.delete({
                tenancy: appContext().tenantId,
                id: this.id
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        if (this.props.params.id)
        {
            await this.updateDataHubCredentials();
        }
        else
        {
            await this.createDataHubCredentials();
        }
    }

    private async updateDataHubCredentials(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.byId.patch<DataHubCredentials>({
                id: this.id,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    AccessKey: this.state.accessKey,
                    ConnectionString: this.state.connectionString,
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async createDataHubCredentials(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.dataHubCredentials.post<DataHubCredentials>({
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    AccessKey: this.state.accessKey,
                    ConnectionString: this.state.connectionString
                }
            });
            this.props.navigate(this.backUrl);
        });
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <LayoutContainer>
                <LayoutContent>
                    <Card
                        title={labels.Platform_DataHubCredentials_DetailsTitle}
                        subTitle={labels.Platform_DataHubCredentials_DetailsSubTitle}
                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                        icon={<SerendieSymbol name='check-shield' />}
                    >
                        <GridContainer>
                            <GridItem>
                                <TextField
                                    required
                                    label={labels.DataHubCredentials_Name}
                                    placeholder={labels.DataHubCredentials_Name}
                                    value={this.state.name}
                                    onChange={i => this.setState({ name: i.target.value }, () => this.validator.validate('name'))}
                                    invalid={!!this.state.nameError}
                                    invalidMessage={this.state.nameError}
                                />
                            </GridItem>
                            <GridItem>
                                <MultilingualTextField
                                    label={labels.DataHubCredentials_DisplayName}
                                    placeholder={labels.DataHubCredentials_DisplayName}
                                    value={this.state.displayName}
                                    onChange={value => this.setState({ displayName: value })}
                                    localisation={appContext().localisation}
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    label={labels.DataHubCredentials_AccessKey}
                                    placeholder={labels.DataHubCredentials_AccessKey}
                                    value={this.state.accessKey}
                                    onChange={e => this.setState({ accessKey: e.target.value })}
                                />
                            </GridItem>
                            <GridItem>
                                <TextArea
                                    label={labels.DataHubCredentials_ConnectionString}
                                    placeholder={labels.DataHubCredentials_ConnectionString}
                                    value={this.state.connectionString}
                                    onChange={e => this.setState({ connectionString: e.target.value })}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                this.props.params.id &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={labels.Platform_DataHubCredential_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(this.backUrl)}
                            >
                                {labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditDataHubCredentials);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    connectionString: string;
    accessKey: string;
    concurrencyToken: string;
    canDelete: boolean;
    sections: IPageNavSection[];
}
